import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { useRecoilValue } from "recoil";
import { useSnackbar } from "notistack";
import { authState } from "../../Recoil/RecoilState";
import {
  confirmLostFoundMatch,
  fetchLostItem,
  fetchLostItemMatches,
  rejectLostFoundMatch,
} from "../../services/lostFoundApi";
import {
  DEFAULT_MIN_MATCH_SCORE,
  FOUND_STATUS_COLOR,
  LOST_STATUS_COLOR,
  errorText,
  formatDateTime,
  formatScore,
  titleCase,
} from "./lostFoundConstants";

const DetailRow = ({ label, children }) => (
  <Stack direction="row" spacing={2} sx={{ py: 0.75 }}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 110, flexShrink: 0 }}>
      {label}
    </Typography>
    <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
  </Stack>
);

/**
 * One candidate. The score breakdown is shown rather than just the total: "81%" tells
 * an operator nothing about whether it came from the picture or from the words.
 */
const MatchCard = ({ match, onConfirm, onReject, busy }) => (
  <Card data-testid="match-card" sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
    {match.crop_path ? (
      <CardMedia component="img" height="170" image={match.crop_path} alt={`Found ${match.class_name}`}
        sx={{ objectFit: "contain", bgcolor: "#000" }} />
    ) : (
      <Box sx={{ height: 170, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "action.hover" }}>
        <Typography variant="caption" color="text.secondary">No crop stored</Typography>
      </Box>
    )}
    <CardContent sx={{ flexGrow: 1 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" fontWeight="bold">{titleCase(match.class_name)}</Typography>
        <Tooltip title={`Raw score ${Number(match.score).toFixed(4)}`}>
          <Chip size="small" color={match.score >= 0.8 ? "success" : match.score >= 0.65 ? "warning" : "default"}
            label={formatScore(match.score)} />
        </Tooltip>
      </Stack>
      <LinearProgress variant="determinate" value={Math.min(100, Number(match.score) * 100)}
        sx={{ mb: 1.5, height: 6, borderRadius: 3 }} />
      <Typography variant="caption" color="text.secondary" display="block">
        {match.camera_name || "Unknown camera"} · found {formatDateTime(match.found_at)}
      </Typography>
      <Stack direction="row" spacing={0.5} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
        {match.same_class && <Chip size="small" variant="outlined" label="Same type" />}
        {match.same_color && <Chip size="small" variant="outlined" label="Same colour" />}
        {match.visual_similarity != null && (
          <Chip size="small" variant="outlined" color={match.visual_similarity >= 0.6 ? "success" : "default"}
            label={`Looks ${Math.round(match.visual_similarity * 100)}% alike`} />
        )}
        {match.sightings > 1 && <Chip size="small" color="info" variant="outlined" label={`Seen ${match.sightings}×`} />}
        {match.dominant_color && <Chip size="small" variant="outlined" label={titleCase(match.dominant_color)} />}
        <Chip size="small" label={titleCase(match.status)} color={FOUND_STATUS_COLOR[match.status] || "default"} />
      </Stack>
    </CardContent>
    <CardActions sx={{ px: 2, pb: 2 }}>
      <Button size="small" variant="contained" color="success" startIcon={<CheckIcon />} disabled={busy}
        onClick={() => onConfirm(match)}>
        This is it
      </Button>
      <Button size="small" color="inherit" startIcon={<CloseIcon />} disabled={busy} onClick={() => onReject(match)}>
        Not it
      </Button>
    </CardActions>
  </Card>
);

/** A lost report and the found items ranked against it. */
const LostItemMatches = ({ lostId, onBack, onChanged }) => {
  const { token } = useRecoilValue(authState);
  const { enqueueSnackbar } = useSnackbar();
  const [report, setReport] = useState(null);
  const [matches, setMatches] = useState(null);
  const [error, setError] = useState(null);
  const [ranking, setRanking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);

  const loadReport = useCallback(async () => {
    try {
      setReport(await fetchLostItem(lostId, token));
    } catch (err) {
      setError(errorText(err, "Could not load the report."));
    }
  }, [lostId, token]);

  // persist=true keeps the shortlist as suggestions, so it can be reviewed later.
  const rank = useCallback(async () => {
    setRanking(true);
    try {
      const data = await fetchLostItemMatches(lostId, token, { persist: true });
      setMatches(data?.matches || []);
    } catch (err) {
      setError(errorText(err, "Matching failed."));
    } finally {
      setRanking(false);
    }
  }, [lostId, token]);

  useEffect(() => {
    loadReport();
    rank();
  }, [loadReport, rank]);

  const confirm = async () => {
    const match = pending;
    setPending(null);
    setBusy(true);
    try {
      await confirmLostFoundMatch({ lostId, foundId: match.found_id, score: match.score }, token);
      enqueueSnackbar("Match confirmed: the report is resolved and the item is claimed.", { variant: "success" });
      loadReport();
      rank();
      if (onChanged) onChanged();
    } catch (err) {
      enqueueSnackbar(errorText(err), { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  const reject = async (match) => {
    setBusy(true);
    try {
      await rejectLostFoundMatch({ lostId, foundId: match.found_id }, token);
      setMatches((current) => (current || []).filter((m) => m.found_id !== match.found_id));
    } catch (err) {
      enqueueSnackbar(errorText(err), { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={onBack}>All reports</Button>
        <Button startIcon={<SearchIcon />} onClick={rank} disabled={ranking}>
          {ranking ? "Searching…" : "Re-run matching"}
        </Button>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={3}>
        <Grid item xs={12} md={4} lg={3}>
          <Card>
            {report?.photo_path ? (
              <CardMedia component="img" height="220" image={report.photo_path} alt={report.description || "Reported item"}
                sx={{ objectFit: "contain", bgcolor: "#000" }} />
            ) : (
              <Box sx={{ height: 120, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "action.hover" }}>
                <Typography variant="caption" color="text.secondary">No photo — description only</Typography>
              </Box>
            )}
            <CardContent>
              {!report ? (
                <CircularProgress size={24} />
              ) : (
                <>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    {report.description || "Photo-only report"}
                  </Typography>
                  <DetailRow label="Status">
                    <Chip size="small" label={titleCase(report.status)} color={LOST_STATUS_COLOR[report.status] || "default"} />
                  </DetailRow>
                  <DetailRow label="Object">{report.class_name ? titleCase(report.class_name) : "Unspecified"}</DetailRow>
                  <DetailRow label="Colour">{report.dominant_color ? titleCase(report.dominant_color) : "—"}</DetailRow>
                  <DetailRow label="Owner">
                    <Typography variant="body2">{report.reporter_name || "—"}</Typography>
                    {report.reporter_email && (
                      <Typography variant="caption" color="text.secondary">{report.reporter_email}</Typography>
                    )}
                  </DetailRow>
                  <DetailRow label="Lost after">{report.lost_after ? formatDateTime(report.lost_after) : "Not narrowed"}</DetailRow>
                  <DetailRow label="Reported">
                    {formatDateTime(report.reported_at)}
                    {report.reported_by_username ? ` by ${report.reported_by_username}` : ""}
                  </DetailRow>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8} lg={9}>
          <Card sx={{ mb: 2, px: 2.5, py: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold">Candidates from the found gallery</Typography>
            <Typography variant="caption" color="text.secondary">
              Unclaimed items found after the loss, scored on the same object type (50%), a matching colour
              (30%), how soon after the loss they turned up (20%) and, with a photo, how alike they look (30%).
              Repeat sightings of one object are shown once; anything below {formatScore(DEFAULT_MIN_MATCH_SCORE)} is hidden.
            </Typography>
          </Card>

          {matches === null ? (
            <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
          ) : matches.length ? (
            <Grid container spacing={2}>
              {matches.map((match) => (
                <Grid item key={match.found_id} xs={12} sm={6} lg={4}>
                  <MatchCard match={match} busy={busy} onConfirm={setPending} onReject={reject} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Card sx={{ p: 5, textAlign: "center" }}>
              <SearchIcon sx={{ fontSize: 44, color: "text.disabled" }} />
              <Typography variant="h6" color="text.secondary">No likely matches yet</Typography>
              <Typography variant="body2" color="text.secondary">
                No unclaimed item found after the loss shares this report&apos;s type or colour. Re-run matching once
                new items come in, or search the Found Items tab by camera and time.
              </Typography>
            </Card>
          )}
        </Grid>
      </Grid>

      <Dialog open={Boolean(pending)} onClose={() => setPending(null)}>
        <DialogTitle>Confirm this match?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The report is marked resolved, the found item claimed, and the alert it came from resolved. The item
            then drops out of future searches.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setPending(null)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={confirm}>Confirm match</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LostItemMatches;
